import * as React from 'react'
import Link, { LinkProps } from 'next/link'
import { cva, type VariantProps } from 'class-variance-authority'
import { ArrowRight, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'

// The reference shell's `.btn` (design overhaul, 2026-10-08): a pill with a
// 15px medium label; hover is a colour change only, nothing moves. Marketing primary
// CTAs use `cta`: Pixelmojo magenta (--cta-fill, white label 4.92:1), the
// same fill src/app/cta.css gives every `.btn` (Lloyd, 2026-10-09). The
// default/action/accent variants stay token-driven because the dashboard
// (PackModal) also renders this component.
// Every colour is a token, so a button inside an ink or accent sheet flips
// with it (ink pill on paper, paper pill on ink).
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-3 whitespace-nowrap rounded-pill border border-transparent font-medium tracking-[-0.01em] transition-[background-color,color,border-color] duration-300 ease-[var(--ease)] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-[18px] [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        // Reference `.btn`: foreground fill, background label.
        default: 'bg-foreground text-background hover:bg-foreground/90',
        action: 'bg-foreground text-background hover:bg-foreground/90',
        // Marketing primary CTA: Pixelmojo magenta.
        cta: 'bg-cta-fill text-white hover:bg-cta-fill-hover',
        // Reference `.btn--accent`: the action blue (paper label 4.68:1).
        accent: 'bg-action text-on-action hover:bg-action/90',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        // Reference `.btn--ghost`: hairline pill that fills on hover.
        outline:
          'border-line-strong bg-transparent text-foreground hover:bg-foreground hover:text-background',
        neutral:
          'border-line-strong bg-transparent text-foreground hover:bg-foreground hover:text-background',
        ghost:
          'border-line-strong bg-transparent text-foreground hover:bg-foreground hover:text-background',
        secondary:
          'bg-muted text-foreground hover:bg-foreground hover:text-background',
        link: 'rounded-none text-primary-ink underline-offset-4 hover:underline',
      },
      size: {
        default: 'min-h-12 px-6 text-[15px]', // reference .btn, 16px 24px
        sm: 'min-h-11 px-[18px] text-sm', // reference .btn--sm, 11px 18px
        lg: 'min-h-14 px-8 text-base',
        icon: 'size-11',
        link: 'h-auto px-0 py-0 text-base', // Text-style links align with surrounding copy
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

// Link Button Props
export interface LinkButtonProps
  extends LinkProps, Omit<VariantProps<typeof buttonVariants>, 'asChild'> {
  className?: string
  children: React.ReactNode
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  style?: React.CSSProperties
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      loading = false,
      leftIcon,
      rightIcon,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const resolvedSize = size ?? (variant === 'link' ? 'link' : undefined)

    const content = loading ? (
      <>
        <svg
          className='animate-spin -ml-1 mr-2 h-5 w-5'
          xmlns='http://www.w3.org/2000/svg'
          fill='none'
          viewBox='0 0 24 24'
          aria-hidden='true'
        >
          <circle
            className='opacity-25'
            cx='12'
            cy='12'
            r='10'
            stroke='currentColor'
            strokeWidth='4'
          />
          <path
            className='opacity-75'
            fill='currentColor'
            d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
          />
        </svg>
        Loading...
      </>
    ) : (
      <>
        {leftIcon && <span className='flex items-center'>{leftIcon}</span>}
        {children}
        {rightIcon && <span className='flex items-center'>{rightIcon}</span>}
      </>
    )

    return (
      <button
        className={cn(
          buttonVariants({ variant, size: resolvedSize, className })
        )}
        disabled={disabled || loading}
        ref={ref}
        {...props}
      >
        {content}
      </button>
    )
  }
)
Button.displayName = 'Button'

// Link Button Component
const LinkButton = React.forwardRef<HTMLAnchorElement, LinkButtonProps>(
  (
    {
      className,
      variant,
      size,
      href,
      children,
      leftIcon,
      rightIcon,
      style,
      ...props
    },
    ref
  ) => {
    const resolvedSize = size ?? (variant === 'link' ? 'link' : undefined)

    const content = (
      <>
        {leftIcon && <span className='flex items-center'>{leftIcon}</span>}
        {children}
        {rightIcon && <span className='flex items-center'>{rightIcon}</span>}
      </>
    )

    return (
      <Link
        className={cn(
          buttonVariants({ variant, size: resolvedSize, className })
        )}
        href={href}
        ref={ref}
        style={style}
        {...props}
      >
        {content}
      </Link>
    )
  }
)
LinkButton.displayName = 'LinkButton'

// Link Button with Arrow Component - FIXED VERSION
export interface LinkButtonWithArrowProps extends Omit<
  LinkButtonProps,
  'rightIcon'
> {
  arrowIcon?: 'chevron' | 'arrow'
  showArrow?: boolean
}

const LinkButtonWithArrow = React.forwardRef<
  HTMLAnchorElement,
  LinkButtonWithArrowProps
>(
  (
    {
      className,
      variant = 'link',
      size,
      href,
      children,
      leftIcon,
      arrowIcon = 'chevron',
      showArrow = true,
      ...props
    },
    ref
  ) => {
    const resolvedSize = size ?? (variant === 'link' ? 'link' : undefined)

    const rightIcon = showArrow ? (
      arrowIcon === 'arrow' ? (
        <ArrowRight size={20} aria-hidden='true' />
      ) : (
        <ChevronRight size={20} aria-hidden='true' />
      )
    ) : null

    // FIXED: Simplified content structure to remove extra wrapper and padding
    const content = (
      <>
        {leftIcon && <span className='flex items-center'>{leftIcon}</span>}
        {children}
        {rightIcon && <span className='inline-block ml-1'>{rightIcon}</span>}
      </>
    )

    return (
      <Link
        className={cn(
          buttonVariants({ variant, size: resolvedSize, className }),
          'group'
        )}
        href={href}
        ref={ref}
        {...props}
      >
        {content}
      </Link>
    )
  }
)
LinkButtonWithArrow.displayName = 'LinkButtonWithArrow'

// Text Button with Arrow (non-link version)
export interface TextButtonWithArrowProps extends Omit<
  ButtonProps,
  'rightIcon'
> {
  arrowIcon?: 'chevron' | 'arrow'
  showArrow?: boolean
}

const TextButtonWithArrow = React.forwardRef<
  HTMLButtonElement,
  TextButtonWithArrowProps
>(
  (
    {
      className,
      variant = 'link',
      size,
      children,
      leftIcon,
      arrowIcon = 'chevron',
      showArrow = true,
      loading = false,
      disabled,
      ...props
    },
    ref
  ) => {
    const resolvedSize = size ?? (variant === 'link' ? 'link' : undefined)

    const rightIcon =
      showArrow && !loading ? (
        arrowIcon === 'arrow' ? (
          <ArrowRight size={20} aria-hidden='true' />
        ) : (
          <ChevronRight size={20} aria-hidden='true' />
        )
      ) : null

    const content = loading ? (
      <>
        <svg
          className='animate-spin -ml-1 mr-2 h-5 w-5'
          xmlns='http://www.w3.org/2000/svg'
          fill='none'
          viewBox='0 0 24 24'
          aria-hidden='true'
        >
          <circle
            className='opacity-25'
            cx='12'
            cy='12'
            r='10'
            stroke='currentColor'
            strokeWidth='4'
          />
          <path
            className='opacity-75'
            fill='currentColor'
            d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
          />
        </svg>
        Loading...
      </>
    ) : (
      <>
        {leftIcon && <span className='flex items-center'>{leftIcon}</span>}
        <span className='inline-flex items-center gap-1 group'>
          {children}
          {rightIcon && <span className='inline-block'>{rightIcon}</span>}
        </span>
      </>
    )

    return (
      <button
        className={cn(
          buttonVariants({ variant, size: resolvedSize, className }),
          'group'
        )}
        disabled={disabled || loading}
        ref={ref}
        {...props}
      >
        {content}
      </button>
    )
  }
)
TextButtonWithArrow.displayName = 'TextButtonWithArrow'

export {
  Button,
  LinkButton,
  LinkButtonWithArrow,
  TextButtonWithArrow,
  buttonVariants,
}
